import "./styleSideNav.scss";
import { ReactComponent as Exit } from "../../../../../../assets/exitIcon.svg";
import CirclePicker from "../../../../../../common/components/inputFields/circlePicker/circlePicker";
import Dropdown from "../../../../../../common/components/inputFields/dropdown/dropdown";
import Button, {
  ButtonTypes,
} from "../../../../../../common/components/button/button";
import { themeColor } from "../../../../../../common/constants/themeColors";
import { fontFamily } from "../../../../../../common/constants/fontFamily";
import { useEffect, useState } from "react";
import { ThemeColorModel, ThemeModel } from "../../../../models/themeModel";
import {
  useErrorHandler,
  useLoadHandler,
} from "../../../../../../common/customHooks/useLoadHandler";
import { CampaignApi } from "../../../../../../common/services/api/campaignApi";
import { MediaType } from "../../../../../../common/enums/mediaTypes";
import { uniqueId } from "lodash";
import { FileApi } from "../../../../../../common/services/api/fileApi";
import { Color } from "../../../../../../common/style/colors";

export default function StyleSideNav(props: {
  themeData?: ThemeModel;
  onClickClose?: () => void;
  onChange?: (e: ThemeModel) => void;
}) {
  const [desktopBackgroundId] = useState<string>(uniqueId());
  const [mobileBackgroundId] = useState<string>(uniqueId());
  const [desktopBackgrounds, setDesktopBackgrounds] = useState<
    ThemeColorModel[]
  >([]);
  const [mobileBackgrounds, setMobileBackgrounds] = useState<ThemeColorModel[]>(
    []
  );
  const colors = themeColor.map((e) => ({
    type: MediaType.COLOR,
    value: e,
  }));
  const font = fontFamily.map((e) => ({
    value: e,
    label: <div style={{ fontFamily: e }}>{e}</div>,
  }));
  const defaultTheme = {
    themeColor: colors[0],
    fontFamily: font[0].value,
    fontColor: colors[9],
    desktopBackground: colors[1],
    mobileBackground: colors[1],
  } as ThemeModel;
  const [themeData, setThemeData] = useState<ThemeModel>(
    props.themeData ?? defaultTheme
  );

  const { callFetchAsync } = useLoadHandler(async () => {
    const backgrounds = await CampaignApi().campaignBackground();
    setDesktopBackgrounds(backgrounds.desktop ?? []);
    setMobileBackgrounds(backgrounds.mobile ?? []);
  });

  const { callFetchAsync: callDeleteBackground } = useErrorHandler(
    async (id: string) => {
      await CampaignApi().deleteCampaignBackground(id);
    }
  );

  useEffect(() => {
    callFetchAsync();
  }, []);

  useEffect(() => {
    props.onChange?.(themeData);
  }, [themeData]);

  const {
    loadingStatus: desktopLoadingStatus,
    callFetchAsync: desktopCallFetchAsync,
  } = useLoadHandler(async (file: File) => {
    let result = await FileApi.uploadFile(file);
    let object = {
      type: file.type.startsWith("image") ? MediaType.IMAGE : MediaType.MP4,
      value: result,
    };
    let result2 = await CampaignApi().newCampaignDesktopBackground(object);
    setDesktopBackgrounds([
      ...desktopBackgrounds,
      { ...object, id: result2.id },
    ]);
  });

  const {
    loadingStatus: mobileLoadingStatus,
    callFetchAsync: mobileCallFetchAsync,
  } = useLoadHandler(async (file: File) => {
    let result = await FileApi.uploadFile(file);
    let object = {
      type: file.type.startsWith("image") ? MediaType.IMAGE : MediaType.MP4,
      value: result,
    };
    let result2 = await CampaignApi().newCampaignMobileBackground(object);
    setMobileBackgrounds([...mobileBackgrounds, { ...object, id: result2.id }]);
  });

  return (
    <div className="style-side-background">
      <div className="style-side-nav">
        <div style={{ alignSelf: "flex-end", cursor: "pointer" }}>
          <Exit
            height={20}
            width={20}
            onClick={props.onClickClose}
            stroke={"black"}
          />
        </div>
        <h3 className="styleTitle">Theme Options</h3>
        <p className="styleDescription">
          You can customize your campaign style by selecting the following
          options!
        </p>
        <hr style={{ width: "100%" }} />
        <CirclePicker
          title="Theme Color"
          editable={false}
          value={themeData.themeColor}
          option={colors}
          onChange={(e) => setThemeData({ ...themeData, themeColor: e })}
        />
        <hr style={{ width: "100%" }} />
        <Dropdown
          initialValue={{
            value: themeData.fontFamily,
            label: themeData.fontFamily,
          }}
          options={font}
          title="Font Family"
          onChange={(e) => setThemeData({ ...themeData, fontFamily: e.value })}
        />
        <hr style={{ width: "100%" }} />
        <CirclePicker
          title="Font Color"
          editable={false}
          value={themeData.fontColor}
          option={colors}
          onChange={(e) => setThemeData({ ...themeData, fontColor: e })}
        />
        <hr style={{ width: "100%" }} />
        <CirclePicker
          title="Desktop Background"
          editable
          infoText="Accept all types of image file / MP4, and optimal size is 4MB (1280*720)"
          value={themeData.desktopBackground}
          option={[...colors, ...desktopBackgrounds]}
          onChange={(e) => setThemeData({ ...themeData, desktopBackground: e })}
          onDelete={(e) => {
            setDesktopBackgrounds(
              [...desktopBackgrounds].filter((e2) => e2 != e)
            );
            callDeleteBackground(e.id);
          }}
        />
        <Button
          type={ButtonTypes.OUTLINED}
          loadSpinColor={Color().primaryColor}
          loadingStatus={desktopLoadingStatus}
          text={"Upload more"}
          onClick={() => document.getElementById(desktopBackgroundId)?.click()}
        />
        <hr style={{ width: "100%" }} />
        <CirclePicker
          title="Mobile Background"
          editable
          infoText="Accept all types of image file / MP4, and optimal size is 3MB (640*360)"
          value={themeData.mobileBackground}
          option={[...colors, ...mobileBackgrounds]}
          onChange={(e) => setThemeData({ ...themeData, mobileBackground: e })}
          onDelete={(e) => {
            setMobileBackgrounds(
              [...mobileBackgrounds].filter((e2) => e2 != e)
            );
            callDeleteBackground(e.id);
          }}
        />
        <Button
          type={ButtonTypes.OUTLINED}
          loadSpinColor={Color().primaryColor}
          loadingStatus={mobileLoadingStatus}
          text={"Upload more"}
          onClick={() => document.getElementById(mobileBackgroundId)?.click()}
        />
        <hr style={{ width: "100%" }} />
      </div>
      <input
        type="file"
        accept="image/*,video/mp4"
        id={desktopBackgroundId}
        style={{ display: "none" }}
        onChange={(e) => desktopCallFetchAsync(e.target.files?.[0])}
      />
      <input
        type="file"
        accept="image/*,video/mp4"
        id={mobileBackgroundId}
        style={{ display: "none" }}
        onChange={(e) => mobileCallFetchAsync(e.target.files?.[0])}
      />
    </div>
  );
}
